import { el } from '@/ui/hud';
import { hintPlacement } from '@/ui/zoom-guard';

/** Above the bottom edge of what is on screen (the undo, forfeit and settings row lives there), in CSS px at the page's own scale. */
const EDGE_GAP_PX = 84;

export interface ZoomHint {
  readonly root: HTMLElement;
  /** True while a zoom the guard could not undo is on: the toast shows and the board takes a pinch. */
  setStuck: (stuck: boolean) => void;
  destroy: () => void;
}

/**
 * The "Pinch out to reset view" toast, in the notification toast's own look (`.w21-notify-toast`),
 * for a zoom the guard could not undo. While zoomed the layout viewport is bigger than what is on
 * screen, so the host is pinned to the visible part and counter-scaled to a constant size.
 *
 * It also marks `<html data-w21-zoom="stuck">`: the board's touch-action is written inline by the
 * renderer, and the stylesheet relaxes it from that attribute.
 */
export function createZoomHint(parent: HTMLElement): ZoomHint {
  const root = el('div', 'w21-zoom-hint');
  root.hidden = true;
  const toast = el('div', 'w21-notify-toast w21-zoom-toast');
  toast.setAttribute('role', 'status');
  const body = el('div', 'w21-nt-body');
  body.append(el('span', 'w21-nt-eyebrow', 'Zoomed in'), el('span', 'w21-nt-line', 'Pinch out to reset view'));
  toast.append(body);
  root.append(toast);
  parent.append(root);

  const vv = window.visualViewport ?? null;
  const place = (): void => {
    if (!vv) return;
    const p = hintPlacement(vv);
    root.style.left = `${p.left}px`;
    root.style.top = `${p.top - EDGE_GAP_PX * p.scale}px`;
    root.style.transform = `scale(${p.scale}) translate(-50%, -100%)`;
  };

  let stuck = false;
  const setStuck = (next: boolean): void => {
    if (next === stuck) return;
    stuck = next;
    root.hidden = !next;
    if (next) {
      document.documentElement.dataset.w21Zoom = 'stuck';
      place();
      vv?.addEventListener('resize', place);
      vv?.addEventListener('scroll', place);
    } else {
      delete document.documentElement.dataset.w21Zoom;
      vv?.removeEventListener('resize', place);
      vv?.removeEventListener('scroll', place);
    }
  };

  return {
    root,
    setStuck,
    destroy() {
      setStuck(false);
      root.remove();
    },
  };
}
